import { useQuery } from '@tanstack/react-query';
import { downloadFile } from '@/features/admin-v2/download';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';

export interface RevenueDoctor {
  id: string;
  name: string;
  branchId: string;
  branchName: string;
}

export interface RevenueTotals {
  visits: number;
  grossSen: number;
  panelSen: number;
  selfPaySen: number;
  doctorShareSen: number;
}

export interface RevenueCalendarDay extends RevenueTotals {
  date: string;
  onRoster: boolean;
}

export interface DoctorRevenuePeriod {
  month: string;
  doctor: RevenueDoctor;
  totals: RevenueTotals;
  days: RevenueCalendarDay[];
}

export type RevenuePayerType = 'panel' | 'self_pay';

export interface RevenueVisit {
  id: string;
  visitNumber: string;
  /** The API supplies a minimised display label, never identity or clinical data. */
  patientDisplay: string;
  closedAt: string;
  payerType: RevenuePayerType;
  payerName: string | null;
  grossSen: number;
  doctorShareSen: number;
}

export interface DoctorRevenueDay {
  date: string;
  onRoster: boolean;
  doctor: RevenueDoctor;
  totals: RevenueTotals;
  visits: RevenueVisit[];
}

const query = (month: string) => `?month=${encodeURIComponent(month)}`;

export const revenueKeys = {
  all: ['doctor-revenue'] as const,
  doctors: (branch: string) => ['doctor-revenue', 'doctors', branch] as const,
  period: (scope: string, month: string, branch: string) =>
    ['doctor-revenue', 'period', scope, month, branch] as const,
  day: (scope: string, date: string, branch: string) =>
    ['doctor-revenue', 'day', scope, date, branch] as const,
};

function useBranchKey() {
  const { selected } = useBranch();
  return selected?.id ?? 'all';
}

export function useRevenueDoctors(enabled = true) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: revenueKeys.doctors(branch),
    queryFn: ({ signal }) =>
      api
        .get<RevenueDoctor[]>('/revenue/doctors', { signal })
        .then((response) => response.data),
    enabled,
  });
}

export function useMyRevenue(month: string) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: revenueKeys.period('me', month, branch),
    queryFn: ({ signal }) =>
      api
        .get<DoctorRevenuePeriod>(`/revenue/me${query(month)}`, { signal })
        .then((response) => response.data),
    enabled: month.length === 7,
  });
}

export function useDoctorRevenue(doctorId: string, month: string) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: revenueKeys.period(doctorId, month, branch),
    queryFn: ({ signal }) =>
      api
        .get<DoctorRevenuePeriod>(
          `/revenue/doctors/${encodeURIComponent(doctorId)}${query(month)}`,
          { signal },
        )
        .then((response) => response.data),
    enabled: doctorId.length > 0 && month.length === 7,
  });
}

export function useMyRevenueDay(date: string | null) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: revenueKeys.day('me', date ?? '', branch),
    queryFn: ({ signal }) =>
      api
        .get<DoctorRevenueDay>(
          `/revenue/me/days/${encodeURIComponent(date!)}`,
          { signal },
        )
        .then((response) => response.data),
    enabled: date !== null,
  });
}

export function useDoctorRevenueDay(doctorId: string, date: string | null) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: revenueKeys.day(doctorId, date ?? '', branch),
    queryFn: ({ signal }) =>
      api
        .get<DoctorRevenueDay>(
          `/revenue/doctors/${encodeURIComponent(doctorId)}/days/${encodeURIComponent(date!)}`,
          { signal },
        )
        .then((response) => response.data),
    enabled: doctorId.length > 0 && date !== null,
  });
}

export function exportMyRevenue(month: string) {
  return downloadFile(
    `/revenue/me/export${query(month)}`,
    `my-revenue-${month}.csv`,
  );
}

export function exportDoctorRevenue(doctorId: string, month: string) {
  return downloadFile(
    `/revenue/doctors/${encodeURIComponent(doctorId)}/export${query(month)}`,
    `doctor-revenue-${month}.csv`,
  );
}
